/**
 * Shared Combined VMT estimator definitions for Transportation, GHG inventory, and populate scripts.
 * Baseline (with population) preserved as COMBINED_2015_2022_LEGACY_COMPONENTS for comparisons.
 */

/** Method version token for cache keys and provenance. */
export const COMBINED_2015_2022_METHOD_VERSION = 'combined_excl_population_rev1';

/** Human-readable label for revised Combined estimator (2015–2022). */
export const COMBINED_2015_2022_METHOD_LABEL =
  'Combined, excluding population — revised';

/** Legacy Combined components (includes population) — baseline for before/after exports. */
export const COMBINED_2015_2022_LEGACY_COMPONENTS = [
  'AADT',
  'EMFAC2025',
  'EMFAC Fleet',
  'EMFAC2021 Fleet',
  'Population',
] as const;

/** Revised Combined components (population removed per Christopher 2026-09-10). */
export const COMBINED_2015_2022_COMPONENTS = [
  'AADT',
  'EMFAC2025',
  'EMFAC Fleet',
  'EMFAC2021 Fleet',
] as const;

export type Combined2015Component = (typeof COMBINED_2015_2022_LEGACY_COMPONENTS)[number];

/** Provenance label for future populate/snapshot writes (not legacy stored `aadt_backcast`). */
export const COMBINED_2015_2022_VMT_SOURCE = 'combined_backcast_excl_population';

/** Legacy stored source label (misleading — actually Combined with population). */
export const COMBINED_2015_2022_LEGACY_VMT_SOURCE = 'aadt_backcast';

/**
 * Average eligible year-over-year scaling ratios using the existing Combined convention.
 * Returns null when no eligible components are present (caller should not substitute zero).
 */
export function averageCombinedScalingFactor(
  ratios: Array<number | null | undefined>
): number | null {
  const eligible = ratios.filter((r): r is number => r != null && r > 0);
  if (eligible.length === 0) return null;
  return eligible.reduce((sum, r) => sum + r, 0) / eligible.length;
}

/** Whether a component participates in the revised Combined average. */
export function isCombined2015ComponentIncluded(
  component: Combined2015Component,
  includePopulation: boolean
): boolean {
  if (component === 'Population') return includePopulation;
  return (COMBINED_2015_2022_COMPONENTS as readonly string[]).includes(component);
}
